/* eslint-disable max-lines */
import { conditionalString, formatNameFirstLast } from '@strata/helper-functions';
import { ChatWithConversationPane } from '@strata/react-chat';
import { getIn } from 'formik';

import { SocketType, wsSend } from 'services/socketHelpers';

export const FMH_UPN = 'ProgramSupport@freemarkethealth.com';
export const MESSAGES_LOCATION_HASH = 'messages';
export const PERF_REF_KEY_NAME = 'performance_vc_reference';

export const FactDataTypes = {
    MESSAGE_TEMPLATE: 'MSG_TMPLT',
    PERFORMANCE_REF: 'PERF_REF',
};

export const PerformanceTypes = {
    AUTOMATIC: 'AUTOMATIC',
    HYBRID: 'HYBRID',
    MANUAL: 'MANUAL',
};

export const ConversationActions = {
    CREATE: 'CREATE',
    CHANGE: 'CHANGE',
};

export const ConversationActionMap = {
    [ConversationActions.CREATE]: {
        title: 'Create Support Conversation',
        actionLabel: 'Create Conversation',
    },
    [ConversationActions.CHANGE]: {
        title: 'Change Conversation Type',
        actionLabel: 'Change Conversation Type',
    },
};

export const ConversationStatusChangeActions = {
    OPEN: 'open',
    CLOSE: 'close',
};

export const ConversationStatusChangeActionMap = {
    [ConversationStatusChangeActions.OPEN]: {
        newStatus: ChatWithConversationPane.ConversationStatus.OPEN,
        actionWord: 'opened',
    },
    [ConversationStatusChangeActions.CLOSE]: {
        newStatus: ChatWithConversationPane.ConversationStatus.CLOSED,
        actionWord: 'closed',
    },
};

export const formatNameMaintainCase = (firstName, lastName) => {
    const firstNameStr = conditionalString(!!firstName, `${firstName} `);
    return `${firstNameStr}${lastName ?? ''}`;
};

export const mapMessage = (message) => ({
    content: message?.content
        ? {
              message: message.content.message,
              status: message.content.status,
              eventStatus: message.content.event_status,
              completedAt: message.content.completed_at,
          }
        : undefined,
    conversationId: message?.conversation_id,
    senderFirstName: message?.first_name,
    isSupportMsg: message?.is_support_msg,
    senderLastName: message?.last_name,
    messageId: message?.message_id,
    messageType: message?.message_type,
    sentTime: message?.sent_time,
    upn: message?.upn,
});

export const mapDatumValue = (conversation, fact) => {
    let mappedValue = fact?.shape?.options_dict?.[fact.datum_value] ?? fact?.datum_value;

    if (fact?.data_type === FactDataTypes.PERFORMANCE_REF) {
        const perfDetails = conversation.details?.[FactDataTypes.PERFORMANCE_REF];
        mappedValue = perfDetails?.[fact.datum_value]?.name ?? fact.datum_value;
    }
    return mappedValue;
};

// eslint-disable-next-line complexity
export const mapConversation = (conversation) => {
    const message = conversation?.last_message ? mapMessage(conversation?.last_message) : undefined;

    const firstFact = conversation.first_fact;

    const datumValue = mapDatumValue(conversation, firstFact);

    const truncDatumValue = datumValue?.substring(0, 200);

    const truncMessage = message?.content?.message?.substring(0, 200);

    return {
        conversationId: conversation.conversation_id,
        displayId: conversation.display_id,
        conversationStatus: conversation.conversation_status,
        createdBySupportUser: conversation.created_by_support_user,
        createdByUpn: conversation.created_by_upn,
        createdByFirstName: conversation.created_by_first_name,
        createdByLastName: conversation.created_by_last_name,
        createdTime: conversation.created_time,
        closedByUpn: conversation.closed_by_upn,
        closedByFirstName: conversation.closed_by_first_name,
        closedByLastName: conversation.closed_by_last_name,
        closedTime: conversation.closed_time,
        details: {
            ...conversation.details,
            subject: conversation.short_title,
        },
        isNote: conversation?.is_note,
        referralId: conversation.referral_id,
        referralFriendlyId: conversation.referral_friendly_id,
        supplierNpi: conversation?.supplier_npi,
        segmentId: conversation?.segment_id,
        benefit: conversation?.benefit,
        topicId: conversation?.topic_id,
        messages: conversation?.messages?.map(mapMessage),
        lastMessage: message && `${message.senderFirstName}: ${truncMessage}`,
        lastUpdatedBy: message?.upn ?? conversation?.created_by_upn,
        isUnread: conversation?.is_unread,
        lastUpdated: conversation?.last_updated,
        subject: {
            title: conversation.short_title,
            isUnread: conversation.is_unread,
        },
        detail: truncDatumValue,
        conversationType: conversation.referral_id ? 'Referral' : 'General',
        createdBy: formatNameFirstLast(
            conversation.created_by_first_name,
            conversation.created_by_last_name,
        ),
        assignedUpn: conversation.assigned_upn,
        assignedFirstName: conversation.assigned_first_name,
        assignedLastName: conversation.assigned_last_name,
        assignedTo: conversation.assigned_upn
            ? formatNameFirstLast(conversation.assigned_first_name, conversation.assigned_last_name)
            : undefined,
        lastUpdatedByFmhUser: conversation.last_updated_by_fmh,
    };
};

export const isFactValid = (fact, facts, details) => {
    if (fact.ancestor_name != null) {
        if (fact.ancestor_name.includes(`${PERF_REF_KEY_NAME}.`)) {
            const perfDetailPath = fact.ancestor_name.slice(PERF_REF_KEY_NAME.length + 1);
            const perfKey = getIn(facts, `${PERF_REF_KEY_NAME}.datum_value`);
            const perfDetails = getIn(details, FactDataTypes.PERFORMANCE_REF);
            if (getIn(perfDetails?.[perfKey], perfDetailPath) === fact.trigger_value) {
                return true;
            }
        } else {
            const ancestorFactKey = Object.keys(facts).find(
                (key) =>
                    facts[key].key_name === fact.ancestor_name && facts[key].datum_value != null,
            );
            if (
                ancestorFactKey &&
                getIn(facts, `${ancestorFactKey}.datum_value`) === fact.trigger_value
            ) {
                return true;
            }
        }
        return false;
    }
    return true;
};

export const getNewFactsReducer = (setDefaults = false) => [
    (prev, curr) => {
        let keyName = curr.key_name;
        if (prev[keyName]) {
            keyName = `${keyName}__${curr.order_index}`;
        }
        return {
            ...prev,
            [keyName]: {
                ...curr,
                ...(curr?.default_value && setDefaults
                    ? {
                          datum_value: curr.default_value,
                      }
                    : {}),
            },
        };
    },
    {},
];

export const detailFactsSort = (a, b) => {
    if (a?.data_type === FactDataTypes.MESSAGE_TEMPLATE) {
        return 1;
    }
    if (b?.data_type === FactDataTypes.MESSAGE_TEMPLATE) {
        return -1;
    }
    return (a?.datum_index ?? 0) - (b?.datum_index ?? 0);
};

export const getDetailFactsReducer = (selectedConversation) => [
    (prev, curr) => {
        const label = prev[curr.label] ? `${curr.label} ${curr.datum_index + 1}` : curr.label;
        return {
            ...prev,
            [label]: mapDatumValue(selectedConversation, curr),
        };
    },
    {},
];

export const reduceCurrentFacts = (prev, curr) => {
    let keyName = curr.key_name;
    const existingFact = prev[keyName];
    if (existingFact) {
        const existingSchema = existingFact.schema_id;
        if (existingSchema !== curr?.schema_id) {
            keyName = `${keyName}__${curr.order_index}`;
        }
    }
    if (curr?.datum_index != null) {
        return {
            ...prev,
            [keyName]: {
                ...(existingFact ?? {}),
                datum_value: [...((existingFact ?? {})?.datum_value ?? []), curr?.datum_value],
            },
        };
    }
    return {
        ...prev,
        [keyName]: {
            ...curr,
            datum_value: curr?.datum_value ?? undefined,
        },
    };
};

export const getApiBodyFactsReducer = (newFacts, details, isChange = false) => [
    (prev, curr) => {
        if (!isFactValid(curr, newFacts, details)) {
            return prev;
        }
        if (isChange && curr?.data_type === FactDataTypes.MESSAGE_TEMPLATE) {
            return prev;
        }
        if (Array.isArray(curr.datum_value)) {
            return [
                ...prev,
                ...curr.datum_value
                    .filter((datum) => datum !== null)
                    .map((value, index) => ({
                        topic_id: curr.topic_id,
                        schema_id: curr.schema_id,
                        fact_id: curr.fact_id,
                        datum_value: value,
                        datum_index: index,
                    })),
            ];
        }
        return [
            ...prev,
            {
                datum_id: curr.datum_id,
                topic_id: curr.topic_id,
                schema_id: curr.schema_id,
                fact_id: curr.fact_id,
                datum_value: curr?.datum_value ?? null,
            },
        ];
    },
    [],
];

// WS calls for User Support to be called with dispatch
export const setUserSupportReferral = (referralId) =>
    wsSend({
        socketType: SocketType.USER_SUPPORT,
        messageType: 'SET-REFERRAL',
        message: {
            referral_id: referralId,
        },
    });

export const setUserSupportConversation = (conversationId) =>
    wsSend({
        socketType: SocketType.USER_SUPPORT,
        messageType: 'SET-CONVERSATION',
        message: {
            conversation_id: conversationId,
        },
    });

export const sendUserSupportMessage = (message) =>
    wsSend({
        socketType: SocketType.USER_SUPPORT,
        messageType: 'SEND-MESSAGE',
        message,
    });

export const sendUserSupportUpdateStatusRequest = (message) =>
    wsSend({
        socketType: SocketType.USER_SUPPORT,
        messageType: 'SET-STATUS',
        message,
    });

export const sendUserSupportUpdateAssigneeRequest = (message) =>
    wsSend({
        socketType: SocketType.USER_SUPPORT,
        messageType: 'SET-ASSIGNEE',
        message,
    });

export const SocketMessageTypes = {
    STATUS: 'STATUS',
    CONVERSATIONS: 'CONVERSATIONS',
    MESSAGES: 'MESSAGES',
    USER_PARTICIPATION: 'USER_PARTI',
    CONVERSATION_PARTICIPATION: 'CONVO_PARTI',
};

/**
 * When conversations would be unread:
 * 1. no participant row, or
 * 3. conversation has last_message_id & participant row doesn't match
 *
 * ReferralConversationCounts looks like:
 * [referralId]: {
 *      open: number,
 *      unread: number,
 *      conversations: {
 *          [conversationId]: boolean, # is_unread: true if unread, false if read
 *     },
 * }
 */

export const getUpdatedReferralConversationCounts = (
    userParticipation,
    conversationParticipation,
) => {
    const newCounts = {};
    Object.values(conversationParticipation ?? {}).forEach((conversation) => {
        const {
            referral_id: referralId,
            conversation_status: conversationStatus,
            conversation_id: conversationId,
            last_message_id: lastMessageId,
        } = conversation;
        const isOpen = conversationStatus === ChatWithConversationPane.ConversationStatus.OPEN;
        if (!newCounts[referralId]) {
            // Initialize if we haven't seen this referral yet
            newCounts[referralId] = {
                open: 0,
                unread: 0,
                conversations: {},
            };
        }
        if (isOpen) {
            newCounts[referralId].open += 1;
        }

        // Evaluate whether the conversation is unread
        if (
            !(conversationId in (userParticipation ?? {})) ||
            (lastMessageId !== null && lastMessageId !== userParticipation?.[conversationId])
        ) {
            // Only count unread if the conversation is open
            if (isOpen) {
                newCounts[referralId].unread += 1;
            }
            newCounts[referralId].conversations[conversationId] = true;
        } else {
            newCounts[referralId].conversations[conversationId] = false;
        }
    });
    return newCounts;
};
